import { useEffect, useMemo, useState } from "react";
import {
  addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek, subMonths,
} from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OptionSelect } from "@/components/FormBits";
import { useBookingDialog } from "@/context/BookingDialogContext";
import { useFacilities } from "@/hooks/useFacilities";
import { api } from "@/lib/api";
import { facilityColor } from "@/lib/constants";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const DayCell = ({ day, month, events, onNew, onEdit }) => {
  const key = format(day, "yyyy-MM-dd");
  return (
    <div
      data-testid={`calendar-day-${key}`}
      onClick={() => onNew(key)}
      className={`min-h-[92px] sm:min-h-[118px] cursor-pointer border-b border-r border-slate-200 p-1.5 sm:p-2 transition-colors hover:bg-[#FDF2F4]/50 ${isSameMonth(day, month) ? "bg-white" : "bg-[#F3F0EA] text-slate-400"}`}
    >
      <span className={`inline-grid h-6 w-6 place-items-center rounded-full font-mono text-xs ${isToday(day) ? "bg-[#8B1E2B] text-white" : ""}`}>{format(day, "d")}</span>
      <div className="mt-1 space-y-1">
        {events.slice(0, 3).map((b) => (
          <button
            key={b.id}
            type="button"
            data-testid={`calendar-event-${b.id}`}
            onClick={(e) => { e.stopPropagation(); onEdit(b); }}
            className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-semibold ${facilityColor(b.facility_slug).chip} ${b.booking_status === "in_attesa" ? "opacity-70 ring-1 ring-dashed ring-white" : ""} ${b.payment_status === "da_pagare" ? "border-l-4 border-amber-300" : ""}`}
          >
            <span className="hidden sm:inline font-mono font-normal mr-1">{b.start_time}</span>{b.title}
          </button>
        ))}
        {events.length > 3 && <p className="text-[11px] text-slate-500">+{events.length - 3} altri</p>}
      </div>
    </div>
  );
};

export default function CalendarPage() {
  const { openNew, openEdit, version } = useBookingDialog();
  const [facilities] = useFacilities();
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [facility, setFacility] = useState("all");
  const [bookings, setBookings] = useState([]);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  }), [month]);

  useEffect(() => {
    const params = { date_from: format(days[0], "yyyy-MM-dd"), date_to: format(days[days.length - 1], "yyyy-MM-dd") };
    if (facility !== "all") params.facility = facility;
    api.get("/bookings", { params }).then((r) => setBookings(r.data.filter((b) => b.booking_status !== "annullata")));
  }, [days, facility, version]);

  const byDay = useMemo(() => bookings.reduce((acc, b) => { (acc[b.date] = acc[b.date] || []).push(b); return acc; }, {}), [bookings]);
  const facOptions = { all: "Tutte le strutture", ...Object.fromEntries(facilities.map((f) => [f.slug, f.name])) };
  const onNew = (date) => openNew({ date, ...(facility !== "all" ? { facility_slug: facility } : {}) });

  return (
    <div data-testid="calendar-page" className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div>
          <p className="eyebrow">Calendario concessioni</p>
          <h1 data-testid="calendar-month-title" className="mt-2 font-serif text-4xl sm:text-5xl font-semibold capitalize text-slate-900">{format(month, "MMMM yyyy", { locale: it })}</h1>
        </div>
        <div className="lg:ml-auto flex flex-wrap items-center gap-2">
          <OptionSelect testId="calendar-facility-filter" className="w-64" value={facility} onChange={setFacility} options={facOptions} />
          <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setMonth(startOfMonth(new Date()))} data-testid="calendar-today-button">Oggi</Button>
          <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-600" data-testid="calendar-legend">
        {facilities.map((f) => <span key={f.slug} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${facilityColor(f.slug).dot}`} />{f.name}</span>)}
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-1 bg-amber-300" />Da pagare</span>
      </div>

      <div className="overflow-hidden rounded-xl border-l border-t border-slate-200">
        <div className="grid grid-cols-7 bg-[#1E293B] text-white">
          {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-center eyebrow !text-slate-300">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => (
            <DayCell key={day.toISOString()} day={day} month={month} events={byDay[format(day, "yyyy-MM-dd")] || []} onNew={onNew} onEdit={openEdit} />
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">Clicca su un giorno per registrare una nuova prenotazione, su un evento per modificarlo.</p>
    </div>
  );
}
